/**
 * The authenticated shell.
 *
 * The session gate lives here rather than in the router: every route needs a
 * user, and a 401 can happen at any moment, so one check at the top is simpler
 * than a per-route guard that each has to remember.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { setUnauthenticatedHandler } from './api/client';
import { keys, useInvalidateContent, useMe } from './api/hooks';
import { Layout } from './components/Layout';
import { LoginPage } from './routes/LoginPage';
import { Spinner } from './components/ui';
import { UploadProvider } from './lib/uploads';

export function App({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const me = useMe();
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);

  // Any 401 anywhere drops the cached identity, which re-renders straight to
  // the sign-in screen instead of leaving a half-broken page behind.
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      client.setQueryData(keys.me, { user: null });
    });
  }, [client]);

  if (me.isLoading) return <Spinner label="Signing in…" />;
  if (!me.data?.user) return <LoginPage />;

  return (
    <UploadsBridge>
      <Layout user={me.data.user} selectedFileIds={selectedFileIds}>
        <SelectionContext.Provider value={setSelectedFileIds}>{children}</SelectionContext.Provider>
      </Layout>
    </UploadsBridge>
  );
}

/** Wires "an upload finished" to "refresh what is on screen". */
function UploadsBridge({ children }: { children: ReactNode }) {
  const invalidate = useInvalidateContent();
  const onUploaded = useCallback(() => invalidate(), [invalidate]);
  return <UploadProvider onUploaded={onUploaded}>{children}</UploadProvider>;
}

import { createContext, useContext } from 'react';

/**
 * Lets the browse screen publish its ticked files upward, so the sidebar tree
 * can accept a drop of them onto a folder.
 */
const SelectionContext = createContext<(ids: string[]) => void>(() => undefined);

export function usePublishSelection() {
  return useContext(SelectionContext);
}
